import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { LaunchDialogService } from '@spartacus/storefront';
import { Observable, Subscription } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { ArticleService } from '../../../../core/catalog';
import { BaseAbstractModalComponent, ModalCloseReason } from '../../../../core/modal';
import { Article, Product } from '../../../../core/model';
import { AreaOfUseAdminService } from '../../../../features/area-of-use-admin-base';

export interface ExcludeArticlesModalData {
  product: Product;
  categoryCode: string;
  excludedArticleCodes: string[];
}

interface Pagination {
  currentScrollIndex: number;
  itemsPerPage: number;
  totalItems: number;
}

@Component({
  selector: 'py-exclude-articles-modal',
  templateUrl: './exclude-articles-modal.component.html',
  styleUrls: ['./exclude-articles-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ExcludeArticlesModalComponent extends BaseAbstractModalComponent implements OnInit, OnDestroy {
  private _initialExcludedArticleCodes: string[];
  private _excludedArticleCodes: string[];
  private subscriptions = new Subscription();

  product: Product;
  categoryCode: string;

  set excludedArticleCodes(excludedArticleCodes: string[]) {
    this._excludedArticleCodes = [...excludedArticleCodes];
    this._initialExcludedArticleCodes = [...excludedArticleCodes];
  }

  get excludedArticleCodes(): string[] {
    return this._excludedArticleCodes;
  }

  get initialExcludedArticleCodes(): string[] {
    return this._initialExcludedArticleCodes;
  }

  pagination: Pagination = {
    currentScrollIndex: 9,
    itemsPerPage: 9,
    totalItems: 0,
  };

  articles$: Observable<Article[]>;

  constructor(
    private articleService: ArticleService,
    private areaOfUseAdminService: AreaOfUseAdminService,
    protected launchDialogService: LaunchDialogService,
    protected el: ElementRef,
    protected renderer: Renderer2
  ) {
    super(el, launchDialogService, renderer);
  }

  get allArticlesSelected(): boolean {
    return !this.product.articles.some((articleCode) => this.excludedArticleCodes.includes(articleCode));
  }

  get allArticlesExcluded(): boolean {
    return this.product.articles.every((articleCode) => this.excludedArticleCodes.includes(articleCode));
  }

  // Returns article codes we need to remove from the store as they will become outdated.
  private get changedArticleCodes(): string[] {
    // Find removed codes
    const removedCodes = this.initialExcludedArticleCodes.filter((code) => !this.excludedArticleCodes.includes(code));
    // Find added codes
    const addedCodes = this.excludedArticleCodes.filter((code) => !this.initialExcludedArticleCodes.includes(code));

    return removedCodes.concat(addedCodes);
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.launchDialogService.data$.pipe(take(1)).subscribe((data: ExcludeArticlesModalData) => {
        this.product = data.product;
        this.categoryCode = data.categoryCode;
        this.excludedArticleCodes = data.excludedArticleCodes;

        this.articles$ = this.articleService.getArticlesFully(this.product.articles).pipe(
          tap((value) => {
            this.pagination.currentScrollIndex = this.pagination.itemsPerPage;
            this.pagination.totalItems = value?.length || 0;
          })
        );
      })
    );
  }

  articleSelected(articleCode: string): boolean {
    return !this.excludedArticleCodes.includes(articleCode);
  }

  save(): void {
    this.areaOfUseAdminService.excludeArticlesFromAreaOfUse(
      this.categoryCode,
      this.excludedArticleCodes,
      this.changedArticleCodes
    );

    this.launchDialogService.closeDialog(ModalCloseReason.EXCLUDE_ARTICLES_CONFIRMED);
  }

  toggleSelectArticle(articleCode: string): void {
    if (this.articleSelected(articleCode)) {
      this._excludedArticleCodes.push(articleCode);
    } else {
      this._excludedArticleCodes = this.excludedArticleCodes.filter((code) => code !== articleCode);
    }
  }

  toggleSelectAll(): void {
    if (this.allArticlesSelected) {
      this._excludedArticleCodes = this.excludedArticleCodes.concat(this.product.articles);
    } else {
      this._excludedArticleCodes = this.excludedArticleCodes.filter((code) => !this.product.articles.includes(code));
    }
  }

  nextScrollIndex(): void {
    if (this.pagination.currentScrollIndex >= this.pagination.totalItems || this.pagination.totalItems === 0) {
      return;
    }

    if (this.pagination.currentScrollIndex + this.pagination.itemsPerPage < this.pagination.totalItems) {
      this.pagination.currentScrollIndex = this.pagination.currentScrollIndex + this.pagination.itemsPerPage;
    } else {
      this.pagination.currentScrollIndex = this.pagination.totalItems;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
